import { formatNumber } from "./crmHelpers";

/*
  Vocabulário das sugestões. Status e tipo vêm do servidor como código
  (PENDING, NEW_FEATURE...); aqui só viram rótulo e cor. O texto que a cliente
  lê no app é outro (está nas traduções do app), mas o sentido é o mesmo.
*/

export const SUGGESTION_STATUS_META = {
  PENDING: { label: "Em espera", tone: "warning" },
  APPROVED: { label: "Aprovada", tone: "info" },
  REJECTED: { label: "Reprovada", tone: "danger" },
  IMPLEMENTED: { label: "Implementada", tone: "success" },
};

export const SUGGESTION_STATUS_ORDER = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "IMPLEMENTED",
];

export const SUGGESTION_KIND_LABELS = {
  NEW_FEATURE: "Funcionalidade nova",
  IMPROVEMENT: "Melhoria",
  OTHER: "Outra ideia",
};

/** Aba do menu do app a que a ideia se refere (códigos de AREAS no backend). */
export const SUGGESTION_AREA_LABELS = {
  GENERAL: "Geral",
  OVERVIEW: "Visão Geral",
  CALENDAR: "Agenda",
  CUSTOMERS: "Clientes",
  COMMANDS: "Comandas",
  CASH_FLOW: "Fluxo de caixa",
  FINANCE: "Financeiro",
  PROFESSIONALS: "Profissionais",
  PRODUCTS: "Produtos",
  SERVICES: "Serviços",
  CATEGORIES: "Categorias",
  GOALS: "Metas e Ranking",
  WALLET: "Carteira",
  REMINDERS: "Lembretes",
  ONLINE_SITE: "Site Online",
  PACKAGES: "Pacotes",
  SUBSCRIPTION_CLUB: "Clube de Assinatura",
  COUPONS: "Cupons",
  WAITING_LIST: "Fila de Espera",
  FORMS: "Formulários",
  REVIEWS: "Avaliações",
};

export const SUGGESTION_SORT_OPTIONS = [
  { value: "recent", label: "Mais recentes" },
  { value: "demand", label: "Mais pedidas" },
];

/** "12 pedidos · 9 contas" (cada conta conta uma vez). */
export const describeDemand = (group) => {
  if (!group) return "";
  const parts = [
    `${formatNumber(group.requests)} ${group.requests === 1 ? "pedido" : "pedidos"}`,
  ];
  if (group.companies > 1) parts.push(`${formatNumber(group.companies)} contas`);
  return parts.join(" · ");
};

const DATE_TIME = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : DATE_TIME.format(date);
};

/*
  Histórico INTERNO. Mostra quem clicou (`actorName`) e, quando houver, quem
  de fato implementou (`implementerName`): a Débora pode marcar como
  implementada o que o Fabio construiu.
*/
export const describeEvent = (event) => {
  const who = event.actorName || "Alguém da equipe";

  switch (event.type) {
    case "STATUS":
      if (event.toStatus === "APPROVED") return `${who} aprovou`;
      if (event.toStatus === "REJECTED") return `${who} reprovou`;
      if (event.toStatus === "PENDING") return `${who} voltou para em espera`;
      if (event.toStatus === "IMPLEMENTED") {
        if (!event.implementerName) return `${who} marcou como implementada`;
        if (event.implementerName === who) {
          return `${who} implementou e marcou como implementada`;
        }
        return `${who} marcou como implementada · implementada por ${event.implementerName}`;
      }
      return `${who} mudou o status`;
    case "IMPLEMENTER":
      return event.implementerName
        ? `${who} informou que ${event.implementerName} implementou`
        : `${who} removeu quem implementou`;
    case "RESPONSE":
      return `${who} editou a resposta`;
    case "MERGE":
    case "UNMERGE":
      return `${event.note || "Junção alterada"} por ${who}`;
    default:
      return who;
  }
};

/**
 * Resumo de uma linha: "Aprovada por Débora · Implementada por Fabio". Lê o
 * histórico (a última aprovação, mesmo que o status já tenha avançado).
 */
export const summarizeHistory = (detail) => {
  const lastTo = (status) =>
    [...(detail.history || [])]
      .reverse()
      .find((event) => event.type === "STATUS" && event.toStatus === status);

  const parts = [];
  const approved = lastTo("APPROVED");
  if (approved && ["APPROVED", "IMPLEMENTED"].includes(detail.status)) {
    parts.push(`Aprovada por ${approved.actorName || "alguém da equipe"}`);
  }

  if (detail.status === "REJECTED") {
    const rejected = lastTo("REJECTED");
    if (rejected) {
      parts.push(`Reprovada por ${rejected.actorName || "alguém da equipe"}`);
    }
  }

  if (detail.status === "IMPLEMENTED") {
    const marked = lastTo("IMPLEMENTED");
    if (detail.implementedBy?.name) {
      parts.push(`Implementada por ${detail.implementedBy.name}`);
      if (marked?.actorName && marked.actorName !== detail.implementedBy.name) {
        parts.push(`marcada por ${marked.actorName}`);
      }
    } else if (marked) {
      parts.push(
        `Marcada como implementada por ${marked.actorName || "alguém da equipe"}`
      );
    }
  }

  return parts.join(" · ");
};
